/**
 * PromotionTermsSection — dynamic, driven off GET /api/promotions/active-terms.
 *
 * Renders the 5-point Saturn Styling promotion terms template per
 * currently-active campaign. Returns null when the API returns an
 * empty list — the section is absent, NOT an empty state message,
 * because there's nothing to disclose when no promo is running.
 *
 * Point 5's final sentence (public-share consent) only appears for
 * single_winner campaigns — a wheel-spin promo has nothing to
 * announce publicly, so it would be irrelevant noise on that mechanic.
 *
 * Freshness: the backend response drives visibility. A kill-switch
 * pause causes the campaign to drop out of /active-terms on the very
 * next fetch — this component fetches once on mount, so any user
 * revisiting the page after a pause sees it gone. No indefinite
 * client-side caching.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IE", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const servicesPhrase = (names) => {
  // null → "all bookable services" (matches template's default).
  if (!names || names.length === 0) return "all bookable services";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

export const TermsBlock = ({ campaign }) => {
  const isWheel = campaign.mechanic_type === "weighted_random";
  const services = servicesPhrase(campaign.applicable_service_names);
  return (
    <article
      className="border border-slate-800 bg-slate-900/60 rounded-lg p-6 mb-6"
      data-testid={`promo-terms-block-${campaign.campaign_id}`}
    >
      <h2 className="text-xl font-bold text-white mb-4">
        Saturn Styling Promotion Terms — {campaign.name}
      </h2>

      <section className="mb-4">
        <h3 className="text-amber-300 text-sm font-semibold mb-1">1. Promoter &amp; eligibility</h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          This promotion is run by Saturn Styling (Duleek, Co. Meath). It applies
          to {services}, booked online between{" "}
          <strong>{fmtDate(campaign.start_date)}</strong> and{" "}
          <strong>{fmtDate(campaign.end_date)}</strong>, while entries last (maximum{" "}
          {campaign.max_entries} entries).
        </p>
      </section>

      <section className="mb-4">
        <h3 className="text-amber-300 text-sm font-semibold mb-1">2. How it works</h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          Eligible bookings are automatically entered into{" "}
          {isWheel
            ? "a randomised prize draw, weighted so rarer prizes are less likely than common ones"
            : "a single-winner prize draw"}
          .{" "}
          {isWheel
            ? "Your result is shown immediately after booking."
            : "One winner will be drawn after the promotion ends and notified directly."}
        </p>
      </section>

      <section className="mb-4">
        <h3 className="text-amber-300 text-sm font-semibold mb-1">3. What discounts mean</h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          Your deposit (already paid) is never refunded or discounted under this
          promotion. Percentage discounts are calculated on the full service price.
          &quot;Free&quot; means no further payment is due on completion — it does not
          refund your deposit.
        </p>
      </section>

      <section className="mb-4">
        <h3 className="text-amber-300 text-sm font-semibold mb-1">4. No cash alternative</h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          There is no cash alternative to any prize. Saturn Styling reserves the
          right to amend or end this promotion at any time.
        </p>
      </section>

      <section>
        <h3 className="text-amber-300 text-sm font-semibold mb-1">5. Data &amp; winner consent</h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          Entries are recorded for prize administration in line with our Privacy
          Policy.{!isWheel && (
            <>
              {" "}If you win, we will ask your separate permission before sharing
              your name or story publicly (e.g. on social media) — you are never
              required to agree, and declining does not affect your prize.
            </>
          )}
        </p>
      </section>

      {campaign.additional_terms && (
        <section
          className="mt-4 pt-4 border-t border-slate-800"
          data-testid={`promo-terms-additional-${campaign.campaign_id}`}
        >
          <h3 className="text-amber-300 text-sm font-semibold mb-1">
            Additional terms for this campaign
          </h3>
          {/* Rendered as plain text, whitespace preserved. HTML is NOT
              interpreted — the admin input is treated as literal
              content. */}
          <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
            {campaign.additional_terms}
          </p>
        </section>
      )}
    </article>
  );
};

const PromotionTermsSection = () => {
  const [campaigns, setCampaigns] = useState(null); // null=loading, []=none, [...]=list

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/promotions/active-terms`)
      .then((r) => {
        if (cancelled) return;
        // HARDENING: any non-array / malformed shape → treat as empty.
        // Matches the banner's contract — a broken endpoint must
        // NEVER render placeholder terms text on the public site.
        const raw = Array.isArray(r?.data) ? r.data : [];
        const clean = raw.filter(
          (c) => c && typeof c.campaign_id === "string" && typeof c.name === "string" && c.name.trim(),
        );
        setCampaigns(clean);
      })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, []);

  // Deliberate: render NOTHING while loading and when list is empty.
  // Spec: "not an empty state message, just absent, since there's
  // nothing to disclose when no promo is running."
  if (!campaigns || campaigns.length === 0) return null;

  return (
    <section
      id="promotion-terms"
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      data-testid="promotion-terms-section"
    >
      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
        Current promotion terms
      </h1>
      <p className="text-slate-400 text-sm mb-6">
        {campaigns.length === 1
          ? "One promotion is currently running. Full terms below."
          : `${campaigns.length} promotions are currently running. Full terms for each below.`}
      </p>
      {campaigns.map((c) => (
        <TermsBlock key={c.campaign_id} campaign={c} />
      ))}
    </section>
  );
};

export default PromotionTermsSection;
